import { db as firebaseDb } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { FormData } from '../components/FormRenderer';
import { uploadPhotosWithCompression, uploadSignature } from './storageService';
import { recordMeasurement } from './measurementService';
import { Inspection as SharedInspection, InspectionStatus as SharedInspectionStatus, InspectionPriority, Role, TemplateMeasurementConfig } from '@easydone/shared';

// Extended local Inspection type with additional UI fields
export interface Inspection extends Omit<SharedInspection, 'status' | 'responses' | 'photos' | 'attachments'> {
  templateName: string;
  assetName?: string;
  assetTag?: string;
  flocName?: string;
  flocCode?: string;
  siteName: string;
  status: InspectionStatus;
  formData: FormData;
  reviewedBy?: string;
  reviewNotes?: string;
}

// Extended status type to include legacy statuses
export type InspectionStatus =
  | SharedInspectionStatus
  | 'submitted'
  | 'reviewed';

export interface CreateInspectionData {
  templateId: string;
  targetType: 'floc' | 'asset';  // What are we inspecting?
  targetId: string;               // FLOC ID or Asset ID
  flocId?: string;                // FLOC context (required if targetType is 'asset')
  assetId?: string;               // Asset ID (required if targetType is 'asset')
  activityClass: 'inspection' | 'maintenance';  // Activity type
  scheduledDate?: Date;
  dueDate?: Date;
  priority?: InspectionPriority;
  assignedTo?: string;
  scheduleId?: string;
  approvalRequired?: boolean;
  approvalLevel?: Role;
  approvalUserId?: string;
  // Legacy fields (deprecated)
  siteId?: string;
}

/**
 * Get all inspections for an organization
 */
export async function getInspections(orgId: string): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const q = query(inspectionsRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Inspection));
}

/**
 * Get inspections by status
 */
export async function getInspectionsByStatus(
  orgId: string,
  status: InspectionStatus
): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const q = query(
    inspectionsRef,
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Inspection));
}

/**
 * Get inspections for an asset
 */
export async function getInspectionsByAsset(
  orgId: string,
  assetId: string
): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const q = query(
    inspectionsRef,
    where('assetId', '==', assetId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Inspection));
}

/**
 * Get recent inspections for an asset (for field review)
 */
export async function getAssetInspections(
  orgId: string,
  assetId: string,
  limitCount: number = 10
): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const q = query(
    inspectionsRef,
    where('assetId', '==', assetId),
    orderBy('completedAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Inspection));
}

/**
 * Get a single inspection
 */
export async function getInspection(
  orgId: string,
  inspectionId: string
): Promise<Inspection | null> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);
  const snapshot = await getDoc(inspectionRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Inspection;
}

/**
 * Create a new inspection
 */
export async function createInspection(
  orgId: string,
  data: CreateInspectionData,
  userId: string,
  userName: string
): Promise<Inspection> {
  // Fetch template
  const template = await getDoc(doc(firebaseDb, 'organizations', orgId, 'templates', data.templateId));

  if (!template.exists()) {
    throw new Error('Template not found');
  }

  const templateData = template.data();

  // Fetch target (FLOC or Asset) information
  let flocId = data.flocId;
  let assetId = data.assetId;
  let flocData: any = null;
  let assetData: any = null;

  if (data.targetType === 'floc') {
    const floc = await getDoc(doc(firebaseDb, 'organizations', orgId, 'functionalLocations', data.targetId));
    if (!floc.exists()) {
      throw new Error('Functional location not found');
    }
    flocData = floc.data();
    flocId = data.targetId;

    // If FLOC has an installed asset, include it
    if (flocData.installedAssetId) {
      assetId = flocData.installedAssetId;
      const asset = await getDoc(doc(firebaseDb, 'organizations', orgId, 'assets', flocData.installedAssetId));
      if (asset.exists()) {
        assetData = asset.data();
      }
    }
  } else if (data.targetType === 'asset') {
    const asset = await getDoc(doc(firebaseDb, 'organizations', orgId, 'assets', data.targetId));
    if (!asset.exists()) {
      throw new Error('Asset not found');
    }
    assetData = asset.data();
    assetId = data.targetId;

    // Get FLOC context if asset is commissioned
    if (assetData.currentFlocId) {
      flocId = assetData.currentFlocId;
      const floc = await getDoc(doc(firebaseDb, 'organizations', orgId, 'functionalLocations', assetData.currentFlocId));
      if (floc.exists()) {
        flocData = floc.data();
      }
    } else if (data.flocId) {
      // Use provided FLOC ID if asset not commissioned
      flocId = data.flocId;
      const floc = await getDoc(doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId));
      if (floc.exists()) {
        flocData = floc.data();
      }
    }
  }

  // Get parent site name if FLOC exists
  let parentSiteName: string | undefined;
  if (flocData) {
    try {
      // If FLOC is already a site, use its name
      if (flocData.type === 'site') {
        parentSiteName = flocData.name;
      } else if (flocData.path && flocData.path.length > 0) {
        // Traverse path to find the root site
        const rootSiteId = flocData.path[0];
        const rootSite = await getDoc(doc(firebaseDb, 'organizations', orgId, 'functionalLocations', rootSiteId));
        if (rootSite.exists()) {
          parentSiteName = rootSite.data().name;
        }
      } else {
        // Fallback to FLOC name if no path
        parentSiteName = flocData.name;
      }
    } catch (error) {
      console.error('Failed to get parent site:', error);
      parentSiteName = flocData.name; // Fallback
    }
  }

  // Initialize form data structure
  const formData: FormData = {
    templateId: data.templateId,
    templateVersion: templateData.version,
    sections: templateData.sections.map((section: any) => ({
      sectionId: section.id,
      fields: section.fields.map((field: any) => ({
        fieldId: field.id,
        value: null,
      })),
    })),
  };

  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const inspectionData = {
    orgId,

    // Target identification
    targetType: data.targetType,
    targetId: data.targetId,
    ...(flocId && { flocId }),
    ...(assetId && { assetId }),

    // Activity classification
    activityClass: data.activityClass,

    // Template
    templateId: data.templateId,
    templateVersion: templateData.version,
    templateName: templateData.name,

    // Status and priority
    status: 'scheduled' as InspectionStatus,
    priority: data.priority || 'routine',

    // Form data
    formData,

    // Inspector
    inspectorId: userId,
    inspectorName: userName,

    // Denormalized fields for UI display
    ...(assetData && {
      assetName: assetData.name,
      assetTag: assetData.assetId, // assetId is the user-defined asset tag/code
    }),
    ...(flocData && {
      flocName: flocData.name,
      flocCode: flocData.code,
    }),
    ...(parentSiteName && { siteName: parentSiteName }), // Show parent site of the FLOC

    // Legacy fields for backward compatibility
    ...(data.siteId && { siteId: data.siteId }),

    // Scheduling
    ...(data.scheduledDate && { scheduledDate: Timestamp.fromDate(data.scheduledDate) }),
    ...(data.dueDate && { dueDate: Timestamp.fromDate(data.dueDate) }),
    ...(data.assignedTo && { assignedTo: data.assignedTo }),
    ...(data.scheduleId && { scheduleId: data.scheduleId }),

    // Approval
    ...(data.approvalRequired !== undefined && { approvalRequired: data.approvalRequired }),
    ...(data.approvalLevel && { approvalLevel: data.approvalLevel }),
    ...(data.approvalUserId && { approvalUserId: data.approvalUserId }),

    // Timestamps
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(inspectionsRef, inspectionData);
  const snapshot = await getDoc(docRef);

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Inspection;
}

/**
 * Start an inspection
 */
export async function startInspection(orgId: string, inspectionId: string): Promise<void> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);
  await updateDoc(inspectionRef, {
    status: 'in_progress',
    startedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update inspection form data
 */
export async function updateInspectionData(
  orgId: string,
  inspectionId: string,
  formData: FormData
): Promise<void> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);
  await updateDoc(inspectionRef, {
    formData,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Record measurements from inspection as separate measurement readings
 */
async function recordMeasurementsFromInspection(
  orgId: string,
  inspectionId: string,
  formData: FormData
): Promise<void> {
  // Get the inspection to access context
  const inspection = await getInspection(orgId, inspectionId);
  if (!inspection) {
    console.error('Inspection not found:', inspectionId);
    return;
  }

  // Get the template to access measurement definitions
  const templateDoc = await getDoc(
    doc(firebaseDb, 'organizations', orgId, 'templates', formData.templateId)
  );
  if (!templateDoc.exists()) {
    console.error('Template not found:', formData.templateId);
    return;
  }

  const template = templateDoc.data();
  const now = Timestamp.now();

  // Iterate through all sections and their measurements
  for (const sectionData of formData.sections) {
    // Skip sections without measurements
    if (!sectionData.measurements || sectionData.measurements.length === 0) {
      continue;
    }

    // Find the corresponding template section to get measurement definitions
    const templateSection = template.sections.find(
      (s: any) => s.id === sectionData.sectionId
    );
    if (!templateSection || !templateSection.measurements) {
      continue;
    }

    // Record each measurement that has a value
    for (const measurementData of sectionData.measurements) {
      // Skip measurements without values
      if (measurementData.value === null || measurementData.value === undefined) {
        continue;
      }

      // Find the measurement definition
      const measurementDef = templateSection.measurements.find(
        (m: TemplateMeasurementConfig) => m.id === measurementData.measurementId
      );
      if (!measurementDef) {
        console.warn('Measurement definition not found:', measurementData.measurementId);
        continue;
      }

      // Calculate status based on thresholds
      let status: 'normal' | 'warning' | 'critical' = 'normal';
      const value = measurementData.value;

      if (typeof value === 'number') {
        // Check critical thresholds first
        if (measurementDef.criticalThreshold) {
          const { min, max } = measurementDef.criticalThreshold;
          if (
            (min !== undefined && value < min) ||
            (max !== undefined && value > max)
          ) {
            status = 'critical';
          }
        }

        // Check warning thresholds if not critical
        if (status === 'normal' && measurementDef.warningThreshold) {
          const { min, max } = measurementDef.warningThreshold;
          if (
            (min !== undefined && value < min) ||
            (max !== undefined && value > max)
          ) {
            status = 'warning';
          }
        }
      }

      // Record the measurement
      try {
        await recordMeasurement(orgId, {
          measurementDefId: measurementDef.definitionId || measurementDef.id,
          measurementName: measurementDef.name,
          measurementCategory: measurementDef.category,

          // Context - FLOC and/or Asset
          ...(inspection.flocId && {
            flocId: inspection.flocId,
            flocName: inspection.flocName,
            flocCode: inspection.flocCode,
          }),
          ...(inspection.assetId && {
            assetId: inspection.assetId,
            assetName: inspection.assetName,
            assetTag: inspection.assetTag,
          }),

          // The measurement value
          value: measurementData.value,
          unit: measurementDef.unit,

          // When and how
          readingDate: now,
          source: 'inspection' as const,
          inspectionId: inspectionId,

          // Who recorded it
          recordedBy: inspection.inspector || 'unknown',
          recordedByName: inspection.inspectorName || 'Unknown',

          // Quality and status
          quality: 'good' as const,
          verified: false,
          status,
        });
      } catch (error) {
        console.error('Failed to record measurement:', error);
        // Continue recording other measurements even if one fails
      }
    }
  }
}

/**
 * Submit an inspection for review
 */
export async function submitInspection(
  orgId: string,
  inspectionId: string,
  formData: FormData
): Promise<void> {
  // Process form data to upload photos and signatures to Storage
  const processedFormData = await processFormDataUploads(orgId, inspectionId, formData);

  // Get current inspection to check if approval is required
  const inspection = await getInspection(orgId, inspectionId);
  const requiresApproval = inspection?.approvalRequired || false;

  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);

  // Prepare update data
  const updateData: any = {
    formData: processedFormData,
    status: requiresApproval ? 'pending_review' : 'approved',
    completedAt: serverTimestamp(),
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // Add maintenance tracking fields if maintenance is required
  if (formData.maintenanceRequired) {
    updateData.maintenanceRequired = formData.maintenanceRequired;
    updateData.maintenanceUrgency = formData.maintenanceUrgency;
    updateData.maintenanceNotes = formData.maintenanceNotes || '';
    updateData.maintenanceReportedBy = inspection?.inspectorName || 'Unknown';
    updateData.maintenanceReportedAt = serverTimestamp();
  } else {
    // Clear maintenance fields if not required
    updateData.maintenanceRequired = false;
    updateData.maintenanceUrgency = null;
    updateData.maintenanceNotes = null;
    updateData.maintenanceReportedBy = null;
    updateData.maintenanceReportedAt = null;
  }

  await updateDoc(inspectionRef, updateData);

  // Record measurements as separate measurement readings
  await recordMeasurementsFromInspection(orgId, inspectionId, processedFormData);
}

/**
 * Process form data to upload photos and signatures to Firebase Storage
 * Replaces base64 data with Storage URLs
 */
async function processFormDataUploads(
  orgId: string,
  inspectionId: string,
  formData: FormData
): Promise<FormData> {
  const processedSections = await Promise.all(
    formData.sections.map(async (section) => {
      const processedFields = await Promise.all(
        section.fields.map(async (field) => {
          // Check if field value contains base64 images (photos)
          if (Array.isArray(field.value) && field.value.length > 0) {
            const isBase64Array = field.value.every(
              (v) => typeof v === 'string' && v.startsWith('data:image/')
            );

            if (isBase64Array) {
              // Upload photos to Storage
              try {
                const photoUrls = await uploadPhotosWithCompression(
                  orgId,
                  inspectionId,
                  field.value
                );
                return { ...field, value: photoUrls };
              } catch (error) {
                console.error('Failed to upload photos:', error);
                // Keep base64 as fallback
                return field;
              }
            }
          }

          // Check if field value is a base64 signature
          if (
            typeof field.value === 'string' &&
            field.value.startsWith('data:image/png')
          ) {
            // Upload signature to Storage
            try {
              const signatureUrl = await uploadSignature(
                orgId,
                inspectionId,
                field.value,
                field.fieldId
              );
              return { ...field, value: signatureUrl };
            } catch (error) {
              console.error('Failed to upload signature:', error);
              // Keep base64 as fallback
              return field;
            }
          }

          return field;
        })
      );

      return { ...section, fields: processedFields };
    })
  );

  return { ...formData, sections: processedSections };
}

/**
 * Review an inspection
 */
export async function reviewInspection(
  orgId: string,
  inspectionId: string,
  reviewerName: string,
  approved: boolean,
  notes?: string
): Promise<void> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);
  await updateDoc(inspectionRef, {
    status: approved ? 'reviewed' : 'failed',
    reviewedBy: reviewerName,
    reviewedAt: serverTimestamp(),
    reviewNotes: notes,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Approve an inspection
 */
export async function approveInspection(
  orgId: string,
  inspectionId: string,
  userId: string,
  userName: string
): Promise<void> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);
  await updateDoc(inspectionRef, {
    status: 'approved',
    approvedBy: userId,
    approvedByName: userName,
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Reject an inspection
 */
export async function rejectInspection(
  orgId: string,
  inspectionId: string,
  userId: string,
  userName: string,
  reason: string
): Promise<void> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);
  await updateDoc(inspectionRef, {
    status: 'rejected',
    approvedBy: userId,
    approvedByName: userName,
    approvedAt: serverTimestamp(),
    rejectionReason: reason,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Cancel an inspection
 */
export async function cancelInspection(
  orgId: string,
  inspectionId: string,
  userId: string,
  userName: string,
  reason: string
): Promise<void> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);
  await updateDoc(inspectionRef, {
    status: 'canceled',
    canceledBy: userId,
    canceledByName: userName,
    canceledAt: serverTimestamp(),
    cancellationReason: reason,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Reschedule an inspection
 * Changes the scheduled date and resets status to 'scheduled'
 */
export async function rescheduleInspection(
  orgId: string,
  inspectionId: string,
  userId: string,
  userName: string,
  newScheduledDate: Date,
  reason: string
): Promise<void> {
  const inspection = await getInspection(orgId, inspectionId);
  const currentCount = inspection?.rescheduleCount || 0;

  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);
  await updateDoc(inspectionRef, {
    status: 'scheduled',
    scheduledDate: Timestamp.fromDate(newScheduledDate),
    dueDate: Timestamp.fromDate(newScheduledDate),
    rescheduledBy: userId,
    rescheduledByName: userName,
    rescheduledAt: serverTimestamp(),
    rescheduleReason: reason,
    rescheduleCount: currentCount + 1,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get inspections for a FLOC
 */
export async function getInspectionsByFloc(
  orgId: string,
  flocId: string
): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const q = query(
    inspectionsRef,
    where('flocId', '==', flocId),
    orderBy('completedAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Inspection));
}

/**
 * Get inspections by activity class
 */
export async function getInspectionsByActivityClass(
  orgId: string,
  activityClass: 'inspection' | 'maintenance'
): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const q = query(
    inspectionsRef,
    where('activityClass', '==', activityClass),
    orderBy('completedAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Inspection));
}

/**
 * Get inspections by target (FLOC or Asset)
 */
export async function getInspectionsByTarget(
  orgId: string,
  targetType: 'floc' | 'asset',
  targetId: string
): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const q = query(
    inspectionsRef,
    where('targetType', '==', targetType),
    where('targetId', '==', targetId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Inspection));
}

/**
 * Backfill measurements from historical inspections
 * This function processes completed inspections and records their measurements
 * as separate measurement readings if they haven't been recorded yet
 */
export async function backfillMeasurementsFromInspections(
  orgId: string,
  options?: {
    startDate?: Date;
    endDate?: Date;
    flocId?: string;
    assetId?: string;
    inspectionIds?: string[];
  }
): Promise<{
  processedCount: number;
  measurementsRecorded: number;
  errors: Array<{ inspectionId: string; error: string }>;
}> {
  const result = {
    processedCount: 0,
    measurementsRecorded: 0,
    errors: [] as Array<{ inspectionId: string; error: string }>,
  };

  try {
    let inspections: Inspection[];

    // If specific inspection IDs provided, fetch those
    if (options?.inspectionIds && options.inspectionIds.length > 0) {
      inspections = [];
      for (const inspectionId of options.inspectionIds) {
        const inspection = await getInspection(orgId, inspectionId);
        if (inspection) {
          inspections.push(inspection);
        }
      }
    } else {
      // Build query based on filters
      const constraints: any[] = [
        where('status', 'in', ['approved', 'completed', 'reviewed']),
      ];

      if (options?.flocId) {
        constraints.push(where('flocId', '==', options.flocId));
      }

      if (options?.assetId) {
        constraints.push(where('assetId', '==', options.assetId));
      }

      if (options?.startDate) {
        constraints.push(
          where('completedAt', '>=', Timestamp.fromDate(options.startDate))
        );
      }

      if (options?.endDate) {
        constraints.push(
          where('completedAt', '<=', Timestamp.fromDate(options.endDate))
        );
      }

      constraints.push(orderBy('completedAt', 'desc'));

      const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
      const q = query(inspectionsRef, ...constraints);
      const snapshot = await getDocs(q);

      inspections = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Inspection));
    }

    // Process each inspection
    for (const inspection of inspections) {
      try {
        if (!inspection.formData || !inspection.formData.sections) {
          continue;
        }

        // Get the template to access measurement definitions
        const templateDoc = await getDoc(
          doc(firebaseDb, 'organizations', orgId, 'templates', inspection.formData.templateId)
        );
        if (!templateDoc.exists()) {
          result.errors.push({
            inspectionId: inspection.id,
            error: 'Template not found',
          });
          continue;
        }

        const template = templateDoc.data();
        const completedAt = inspection.completedAt || Timestamp.now();

        let measurementCount = 0;

        // Process each section
        for (const sectionData of inspection.formData.sections) {
          if (!sectionData.measurements || sectionData.measurements.length === 0) {
            continue;
          }

          const templateSection = template.sections.find(
            (s: any) => s.id === sectionData.sectionId
          );
          if (!templateSection || !templateSection.measurements) {
            continue;
          }

          // Record each measurement
          for (const measurementData of sectionData.measurements) {
            if (measurementData.value === null || measurementData.value === undefined) {
              continue;
            }

            const measurementDef = templateSection.measurements.find(
              (m: TemplateMeasurementConfig) => m.id === measurementData.measurementId
            );
            if (!measurementDef) {
              continue;
            }

            // Calculate status
            let status: 'normal' | 'warning' | 'critical' = 'normal';
            const value = measurementData.value;

            if (typeof value === 'number') {
              if (measurementDef.criticalThreshold) {
                const { min, max } = measurementDef.criticalThreshold;
                if (
                  (min !== undefined && value < min) ||
                  (max !== undefined && value > max)
                ) {
                  status = 'critical';
                }
              }

              if (status === 'normal' && measurementDef.warningThreshold) {
                const { min, max } = measurementDef.warningThreshold;
                if (
                  (min !== undefined && value < min) ||
                  (max !== undefined && value > max)
                ) {
                  status = 'warning';
                }
              }
            }

            // Record the measurement
            try {
              await recordMeasurement(orgId, {
                measurementDefId: measurementDef.definitionId || measurementDef.id,
                measurementName: measurementDef.name,
                measurementCategory: measurementDef.category,

                ...(inspection.flocId && {
                  flocId: inspection.flocId,
                  flocName: inspection.flocName,
                  flocCode: inspection.flocCode,
                }),
                ...(inspection.assetId && {
                  assetId: inspection.assetId,
                  assetName: inspection.assetName,
                  assetTag: inspection.assetTag,
                }),

                value: measurementData.value,
                unit: measurementDef.unit,

                readingDate: completedAt,
                source: 'inspection' as const,
                inspectionId: inspection.id,

                recordedBy: inspection.inspector || 'unknown',
                recordedByName: inspection.inspectorName || 'Unknown',

                quality: 'good' as const,
                verified: false,
                status,
              });

              measurementCount++;
            } catch (error) {
              console.error(`Failed to record measurement for inspection ${inspection.id}:`, error);
            }
          }
        }

        result.processedCount++;
        result.measurementsRecorded += measurementCount;
      } catch (error: any) {
        result.errors.push({
          inspectionId: inspection.id,
          error: error.message || 'Unknown error',
        });
      }
    }
  } catch (error: any) {
    throw new Error(`Failed to backfill measurements: ${error.message}`);
  }

  return result;
}

/**
 * Reserve an inspection for offline work
 */
export async function reserveInspection(
  orgId: string,
  inspectionId: string,
  userId: string,
  userName: string
): Promise<void> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);

  await updateDoc(inspectionRef, {
    reservedBy: userId,
    reservedByName: userName,
    reservedAt: serverTimestamp(),
  });
}

/**
 * Unreserve an inspection
 */
export async function unreserveInspection(
  orgId: string,
  inspectionId: string
): Promise<void> {
  const inspectionRef = doc(firebaseDb, 'organizations', orgId, 'inspections', inspectionId);

  await updateDoc(inspectionRef, {
    reservedBy: null,
    reservedByName: null,
    reservedAt: null,
  });
}

/**
 * Get available inspections (not reserved, scheduled status)
 */
export async function getAvailableInspections(orgId: string): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  // Simplified query - filter unreserved in code to avoid composite index issues
  const q = query(
    inspectionsRef,
    where('status', '==', 'scheduled'),
    orderBy('scheduledDate', 'asc')
  );

  const snapshot = await getDocs(q);

  // Filter out reserved inspections in code
  return snapshot.docs
    .filter(doc => {
      const data = doc.data();
      // Include only if reservedBy is null, undefined, or empty
      return !data.reservedBy;
    })
    .map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        // Ensure required fields have default values
        responses: data.responses || {},
        photos: data.photos || [],
        attachments: data.attachments || [],
      } as unknown as Inspection;
    });
}

/**
 * Get inspections reserved by a specific user
 */
export async function getReservedInspections(
  orgId: string,
  userId: string
): Promise<Inspection[]> {
  const inspectionsRef = collection(firebaseDb, 'organizations', orgId, 'inspections');
  const q = query(
    inspectionsRef,
    where('reservedBy', '==', userId),
    orderBy('scheduledDate', 'asc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      // Ensure required fields have default values
      responses: data.responses || {},
      photos: data.photos || [],
      attachments: data.attachments || [],
    } as unknown as Inspection;
  });
}
